import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { generateFIN } from '../../utils/finUtils';
import { saveEncounterHistory } from '../../services/encounterHistoryService';
import { 
  createEncounter as createEncounterService,
  updateEncounter as updateEncounterService,
  getPatientEncounters as getPatientEncountersService,
  getFacilityEncounters as getFacilityEncountersService,
  getRecentEncounters as getRecentEncountersService,
  getEncounterById as getEncounterByIdService
} from '../../services/encounterService';
import { useUserData } from '../../hooks/useUserData';
/**
 * Comprehensive data sanitization utility to convert Firestore Timestamps and handle non-serializable data
 * @param {any} data - Data to sanitize
 * @returns {any} Sanitized data
 */
const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  // Handle arrays separately
  if (Array.isArray(data)) {
    return data.map(item => 
      typeof item === 'object' ? sanitizeData(item) : item
    );
  }
  
  // Handle objects
  const sanitized = { ...data };
  
  Object.keys(sanitized).forEach(key => {
    const value = sanitized[key];
    
    // Check if value is a Firestore Timestamp
    if (value && typeof value === 'object' && value._seconds !== undefined && value._nanoseconds !== undefined) {
      // Convert Timestamp to ISO string for Redux serialization
      const date = new Date(value._seconds * 1000 + value._nanoseconds / 1000000);
      sanitized[key] = date.toISOString();
    } 
    // Handle Date objects - convert to ISO string for Redux serialization
    else if (value instanceof Date) {
      sanitized[key] = value.toISOString();
    }
    // Handle nested objects and arrays
    else if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        sanitized[key] = value.map(item => 
          typeof item === 'object' ? sanitizeData(item) : item
        );
      } else {
        sanitized[key] = sanitizeData(value);
      }
    }
  });
  
  return sanitized;
};

const initialState = {
  encounters: [],
  selectedEncounter: null,
  recentEncounters: [],
  loading: false,
  error: null
};

// Async thunks
export const updateEncounter = createAsyncThunk(
  'encounters/updateEncounter',
  async ({ encounterId, updateData }) => {
    try {
      // Add updatedAt timestamp
      const dataToUpdate = {
        ...updateData,
        updatedAt: new Date().toISOString()
      };
      
      // Update the encounter using service layer
      const updatedEncounter = await updateEncounterService(encounterId, dataToUpdate);
      
      // Save encounter history (this runs async and won't block the main update)
      try {
        const { user } = useUserData();
        const userId = user?.uid || 'system';
        await saveEncounterHistory(encounterId, dataToUpdate, userId, 'update');
      } catch (historyError) {
        console.error('Error saving encounter history:', historyError);
        // Don't throw - history failure shouldn't break the main update
      }
      
      // Return the updated data
      return updatedEncounter;
    } catch (error) {
      throw error;
    }
  }
);

const encountersSlice = createSlice({
  name: 'encounters',
  initialState,
  reducers: {
    setEncounters: (state, action) => {
      // Extract encounters from payload if it's wrapped in an object
      let encountersToStore = action.payload;
      if (action.payload && typeof action.payload === 'object' && action.payload.encounters) {
        encountersToStore = action.payload.encounters;
      }
      
      // Handle case where encounters might be an object with numeric keys instead of an array
      if (encountersToStore && typeof encountersToStore === 'object' && !Array.isArray(encountersToStore)) {
        // Convert object with numeric keys to array
        encountersToStore = Object.values(encountersToStore);
      }
      
      // Ensure we have an array before sanitizing
      if (!Array.isArray(encountersToStore)) {
        encountersToStore = [];
      }
      
      // Sanitize data before storing in Redux state
      const sanitizedEncounters = sanitizeData(encountersToStore);
      
      // Ensure the sanitized result is still an array
      if (Array.isArray(sanitizedEncounters)) {
        state.encounters = sanitizedEncounters;
      } else {
        state.encounters = [];
      }
      
      state.loading = false;
      state.error = null;
    },
    setSelectedEncounter: (state, action) => {
      // Sanitize data before storing in Redux state
      state.selectedEncounter = sanitizeData(action.payload);
      state.loading = false;
      state.error = null;
    },
    setRecentEncounters: (state, action) => {
      // Sanitize data before storing in Redux state
      state.recentEncounters = sanitizeData(action.payload);
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateEncounter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEncounter.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Sanitize the payload data before updating state
        const sanitizedPayload = sanitizeData(action.payload);
        // Update the selected encounter with the new data
        if (state.selectedEncounter && state.selectedEncounter.id === sanitizedPayload.id) {
          const mergedEncounter = { ...state.selectedEncounter, ...sanitizedPayload };
          state.selectedEncounter = sanitizeData(mergedEncounter);
        }
        // Also update the encounter in the encounters list if it exists
        const encounterIndex = state.encounters.findIndex(e => e.id === sanitizedPayload.id);
        if (encounterIndex !== -1) {
          state.encounters[encounterIndex] = { ...state.encounters[encounterIndex], ...sanitizedPayload };
        }
      })
      .addCase(updateEncounter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createEncounter.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Sanitize the new encounter data before adding to state
        const sanitizedEncounter = sanitizeData(action.payload);
        state.encounters.unshift(sanitizedEncounter);
      })
      .addCase(createEncounter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createNewEncounter.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Sanitize the new encounter data before adding to state
        const sanitizedEncounter = sanitizeData(action.payload);
        state.encounters.unshift(sanitizedEncounter);
      })
      .addCase(createNewEncounter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const {
  setEncounters,
  setSelectedEncounter,
  setRecentEncounters,
  setLoading,
  setError
} = encountersSlice.actions;

// Thunks
export const createEncounter = createAsyncThunk(
  'encounters/createEncounter',
  async (encounterData) => {
    try {
      const newEncounterData = await createEncounterService({
        ...encounterData,
        fin: generateFIN()
      });
      return newEncounterData;
    } catch (error) {
      throw error;
    }
  }
);

export const fetchPatientEncounters = (patientId, facilityId = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    
    // Build options with facilityId for proper data isolation
    const options = {};
    if (facilityId) {
      options.facilityId = facilityId;
    }
    
    const response = await getPatientEncountersService(patientId, options);

    // Extract encounters from response and handle data structure
    let encounters = [];
    if (response && response.encounters) {
      encounters = response.encounters;
    } else if (Array.isArray(response)) {
      encounters = response;
    }

    // Sanitize data before storing in Redux state
    const sanitizedEncounters = Array.isArray(encounters)
      ? encounters.map(encounter => sanitizeData(encounter))
      : [];

    dispatch(setEncounters(sanitizedEncounters));
  } catch (error) {
    console.error('Error fetching patient encounters:', error);
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchAllEncounters = (facilityIds) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    // Get the first facility ID from the array
    const facilityId = Array.isArray(facilityIds) ? facilityIds[0] : facilityIds;
    
    if (!facilityId) {
      dispatch(setLoading(false));
      return;
    }
    
    // Use the dedicated facility encounters endpoint with summary mode for lightweight list view
    // Increased limit to 500 to ensure we capture enough encounters for client-side filtering
    let response;
    try {
      response = await getFacilityEncountersService(facilityId, {
        limit: 500,
        summary: true  // Only fetch fields needed for list view
      });
    } catch (error) {
      throw error;
    }
    
    // Extract encounters from response and handle data structure
    let encounters = [];
    if (response && response.encounters) {
      encounters = response.encounters;
    } else if (Array.isArray(response)) {
      encounters = response;
    }
    
    // Sanitize data before storing in Redux state
    const sanitizedEncounters = Array.isArray(encounters) 
      ? encounters.map(encounter => sanitizeData(encounter))
      : [];
    
    dispatch(setEncounters(sanitizedEncounters));
  } catch (error) {
    console.error('Error fetching facility encounters:', error);
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchRecentEncounters = (patientId, options = {}) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await getRecentEncountersService(options);
    
    // Extract encounters from response and handle data structure
    let encounters = [];
    if (response && response.encounters) {
      encounters = response.encounters;
    } else if (Array.isArray(response)) {
      encounters = response;
    }
    
    // Sanitize data before storing in Redux state
    const sanitizedEncounters = Array.isArray(encounters) 
      ? encounters.map(encounter => sanitizeData(encounter))
      : [];
    
    dispatch(setRecentEncounters(sanitizedEncounters));
  } catch (error) {
    console.error('Error fetching recent encounters:', error);
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchEncounterById = (encounterId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const encounter = await getEncounterByIdService(encounterId);
    if (encounter) {
      // Sanitize data before storing in Redux state
      const sanitizedEncounter = sanitizeData(encounter);
      dispatch(setSelectedEncounter(sanitizedEncounter));
    } else {
      dispatch(setError('Encounter not found'));
    }
  } catch (error) {
    console.error('Error fetching encounter:', error);
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

export const createNewEncounter = createAsyncThunk(
  'encounters/createNewEncounter',
  async (encounterData) => {
    try {
      // Create a new encounter using the createEncounter service
      const newEncounterData = await createEncounterService({
        ...encounterData,
        fin: generateFIN(),
        legacyEncounter: true // mark as legacy encounter data model
      });
      return newEncounterData;
    } catch (error) {
      throw error;
    }
  }
);

export default encountersSlice.reducer;
